import {
	ErpScenarioDetailResponse,
	ErpScenarioResponse,
	ErpSessionSimpleResponse,
	PaginatedItemsResponse,
	WrappedErpSessionDetailResponse,
} from 'com.cogvio.pricemonitor.api';
import { apiClientMutator } from '~/infrastructure/config/mutator';
import { ERPDistributionType, ERPInterventionEditData, MessyRequestBody } from '../../schemas/erp';
import { VolumeGrowthPrice } from '~/pages/IRPDetail/src/components/PriceVolumeGrowthModalRow';
import { DEFAULT_DECIMAL } from '~/UI/Input/DecimalInput';

export function getErpSessions(userId: string) {
	const fetchData = async () => {
		const data = await apiClientMutator<PaginatedItemsResponse<ErpSessionSimpleResponse>>({
			url: `/users/${userId}/erp/sessions`,
			method: 'get',
		});
		return data;
	};
	return fetchData();
}

export function getErpPackages(userId: string, virtualPackId: string, sessionId: string) {
	const fetchData = async () => {
		const data = await apiClientMutator<WrappedErpSessionDetailResponse>({
			url: `/users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}`,
			method: 'get',
		});
		return data;
	};
	return fetchData();
}

export function getErpScenario(userId: string, virtualPackId: string, sessionId: string, scenarioId: string) {
	const fetchData = async () => {
		const data = await apiClientMutator<ErpScenarioDetailResponse>({
			url: `/users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}/scenarios/${scenarioId}`,
			method: 'get',
		});
		return data;
	};
	return fetchData();
}

export function createErpScenarioIntervention(
	userId: string,
	virtualPackId: string,
	sessionId: string,
	scenarioName: string
) {
	const fetchData = async () => {
		const data = await apiClientMutator<ErpScenarioResponse>({
			url: `/users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}/scenarios`,
			method: 'post',
			data: JSON.stringify(scenarioName),
		});
		return data;
	};
	return fetchData();
}

export function setErpScenarioInterventions(
	userId: string,
	virtualPackId: string,
	sessionId: string,
	scenarioId: string,
	interventionData: ERPInterventionEditData[]
) {
	const fetchData = async () => {
		const data = await apiClientMutator<ERPInterventionEditData>({
			url: `/users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}/scenarios/${scenarioId}/interventions`,
			method: 'put',
			data: JSON.stringify(interventionData),
		});
		return data;
	};
	return fetchData();
}

export function setErpVolumeGrowthPrices(
	userId: string,
	virtualPackId: string,
	sessionId: string,
	changedPrices: VolumeGrowthPrice[]
) {
	const fetchData = async () => {
		const data = await apiClientMutator<ERPInterventionEditData>({
			url: `/users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}/prices`,
			method: 'put',
			data: JSON.stringify({
				changedPrices: changedPrices
					.filter(({ isEdited }) => isEdited)
					.map(({ country, distributionType, isUserDefined, selectedPrice, price, currency }) => {
						return {
							country,
							[isUserDefined ? 'userPrice' : 'sourcePriceId']: isUserDefined
								? {
										price: {
											value: Number(price),
											currency,
										},
										distributionType: distributionType ?? ERPDistributionType.RETAIL,
								  }
								: selectedPrice,
						};
					}),
			}),
		});
		return data;
	};
	return fetchData();
}

const prepareRequestBody = (
	messyBody: MessyRequestBody
): {
	yoyGrowth: string;
	yearlyVolume: string;
	countryCode: string;
}[] =>
	messyBody
		.filter(
			(value): value is { countryCode: string; yearlyVolume: string; yoyGrowth: string } =>
				typeof value.yoyGrowth !== 'undefined' && typeof value.yearlyVolume !== 'undefined'
		)
		.map(({ countryCode, yoyGrowth, yearlyVolume }) => ({
			yoyGrowth: (Number(yoyGrowth) / 100).toFixed(DEFAULT_DECIMAL + 2),
			yearlyVolume: yearlyVolume,
			countryCode,
		}));

export function setErpVolumeGrowthCountryFigures(
	userId: string,
	virtualPackId: string,
	sessionId: string,
	body: MessyRequestBody
) {
	const fetchData = async () => {
		const requestBody = body.filter(({ yearlyVolume }) => yearlyVolume !== '');
		const data = await apiClientMutator<ERPInterventionEditData>({
			url: `/users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}/country-figures`,
			method: 'put',
			data: JSON.stringify(prepareRequestBody(requestBody)),
		});
		return data;
	};
	return fetchData();
}
